import { BULK_ACTION_BATCH_SIZE } from '@tanko/shared'
import { errorText, runSequentially, type BulkOutcome } from './bulk-progress'
import type { LibraryEntry } from './library-entries'

/**
 * 批次移除。
 *
 * 一次選取可能同時含「只在雲端」與「在這台裝置上」的作品，兩者走不同的儲存體，跨兩個
 * 儲存體的交易本來就不存在，因此各走各的路再把結果合起來回報。
 *
 * 雲端那一段是一次請求、原子套用：失敗時那一段一本都沒刪，沒有「已刪幾本」可報。本機
 * 那一段仍然是逐本的 Dexie 交易，因此保留「做到哪裡」。
 */
export type RemovalActions = {
  /** 從這台裝置的書庫移除，硬碟上的檔案不動。 */
  removeLocal: (localId: string) => Promise<void>
  /** 刪除雲端副本。一次收下整段。 */
  deleteCloud: (comicIds: string[]) => Promise<void>
}

function batches<T>(items: readonly T[]): T[][] {
  const result: T[][] = []
  for (let index = 0; index < items.length; index += BULK_ACTION_BATCH_SIZE) {
    result.push(items.slice(index, index + BULK_ACTION_BATCH_SIZE))
  }
  return result
}

export async function removeEntries(entries: readonly LibraryEntry[], actions: RemovalActions): Promise<BulkOutcome> {
  const completedIds: string[] = []
  // 只在雲端的那些：這台裝置沒有檔案，唯一的一份就是雲端副本。
  const cloudOnly = entries.filter(entry => !entry.local)
  for (const batch of batches(cloudOnly)) {
    try { await actions.deleteCloud(batch.map(entry => entry.comic.id)) }
    catch (cause) { return { completed: completedIds.length, completedIds, reason: errorText(cause), cause } }
    completedIds.push(...batch.map(entry => entry.id))
  }

  const mine = entries.filter(entry => entry.local)
  const byId = new Map(mine.map(entry => [entry.id, entry]))
  const local = await runSequentially([...byId.keys()], async id => {
    // 已不存在的作品視為已完成，不算失敗。
    const entry = byId.get(id)
    if (entry?.local) await actions.removeLocal(entry.local.id)
  })
  completedIds.push(...local.completedIds)
  return { completed: completedIds.length, completedIds, reason: local.reason, cause: local.cause }
}
